import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { App as AntdApp } from "antd";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { AccountSkeleton } from "./components/accountSkeleton.js";
import { FatalError } from "./components/fatalError.js";
import { SlimHero } from "./components/hero/slimHero.js";
import { DetailsSkeleton } from "./components/tabs/details/detailsSkeleton.js";
import { InvoicesSkeleton } from "./components/tabs/invoices/invoicesSkeleton.js";
import { TabStrip } from "./components/tabs/tabStrip.js";
import { MagicLinkSignInForm } from "./components/unauthenticated/magicLinkSignInForm.js";
import { UnauthAlternativePaths } from "./components/unauthenticated/unauthAlternativePaths.js";
import { UnauthFeatures } from "./components/unauthenticated/unauthFeatures.js";
import { UnauthHero } from "./components/unauthenticated/unauthHero.js";
import { useAccountMagicLinkToken } from "./hooks/useAccountMagicLinkToken.js";
import { useAccountV2ErrorHandling } from "./hooks/useAccountV2ErrorHandling.js";
import { useUserConfirmationCode } from "./hooks/useUserConfirmationCode.js";
import { Notifications } from "../../components/notifications.js";
import { RealCommerceError } from "../../exceptions/realCommerceError.js";
import { useAccountEmailConfirmationToken } from "../../hooks/useAccountEmailConfirmationToken.js";
import { trackAnalyticsEvent } from "../../others/tracking.js";
import { NotificationChannel } from "../../store/notification.js";
import { useStores } from "../../store/stores.js";
import { __ } from "../../utils/i18n.js";
import { LOCAL_STORAGE_JWT_TOKEN_NAME } from "../../utils/jwtTokenStorage.js";
// JWT bootstrap lives at this level (not in MagicLinkSignInForm) so it
// runs even when we go straight from hydrating-skeleton to auth view
// without ever showing the sign-in form.
//
// Update `AccountSkeleton.tsx` alongside any logged-in layout change so
// the skeleton swap stays zero-shift.
const App = observer(()=>{
    const { accountV2Store } = useStores();
    const { message } = AntdApp.useApp();
    const handleError = useAccountV2ErrorHandling();
    const { isLoggedIn, isHydrating, fatalError } = accountV2Store;
    useEffect(()=>{
        trackAnalyticsEvent("customer_center_loaded", {
            ga: {
                event_category: "account",
                event_label: "Customer center loaded"
            },
            matomo: {
                category: "account",
                name: "Customer center loaded"
            }
        });
    }, []);
    const { magicLinkToken, jwt, erase, saveJwt } = useAccountMagicLinkToken(async (magicLinkToken)=>{
        try {
            return await accountV2Store.receiveJwt(magicLinkToken);
        } catch (e) {
            // Backend down on JWT exchange → escalate to the full-page
            // fatal-error view; per-error messages (invalid token, etc.)
            // still throw so the caller's own flow can react.
            if (e instanceof RealCommerceError && (e.isBackendDown || e.status === 500)) {
                accountV2Store.setFatalError(e);
            }
            throw e;
        }
    }, LOCAL_STORAGE_JWT_TOKEN_NAME);
    // The hook strips the token from the URL before calling us, so a reload
    // cannot retry — every outcome has to be surfaced here, success and failure
    // alike, or a failed confirmation leaves the user with no feedback.
    useAccountEmailConfirmationToken(async (token)=>{
        const hide = message.loading(__("Confirming your new email address..."));
        try {
            await accountV2Store.updateEmailAddress(token);
            message.success(__("Your email address has been updated."));
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleError(e);
            }
        } finally{
            hide();
        }
    });
    const { isBusy: doubleOptInBusy, result: doubleOptInResult } = useUserConfirmationCode();
    useEffect(()=>{
        if (doubleOptInResult) {
            saveJwt(doubleOptInResult.jwt);
        }
    }, [
        doubleOptInResult,
        saveJwt
    ]);
    useEffect(()=>{
        if (jwt) {
            accountV2Store.createSessionFromJwt(jwt).catch((e)=>{
                if (e instanceof RealCommerceError) {
                    if (e.original?.status === 401) {
                        // Expired/invalid session: drop the stale JWT and
                        // fall through to the unauth view so the customer
                        // can re-request a magic link. No fatal page —
                        // this is a recoverable condition.
                        erase();
                    } else if (e.isBackendDown || e.status === 500) {
                        accountV2Store.setFatalError(e);
                    }
                }
            }).finally(()=>accountV2Store.finishHydration());
        }
    }, [
        jwt
    ]);
    // No credentials in flight (no URL token, no stored JWT, no DOI code) and
    // the constructor already flagged us as non-hydrating — nothing to do.
    // But the magic-link-token / DOI-code paths may still resolve later, so
    // only finish hydration once their pending work is gone too.
    useEffect(()=>{
        if (!jwt && !magicLinkToken && !doubleOptInBusy && accountV2Store.isHydrating) {
            accountV2Store.finishHydration();
        }
    }, [
        jwt,
        magicLinkToken,
        doubleOptInBusy
    ]);
    if (fatalError) {
        return /*#__PURE__*/ _jsx("main", {
            "aria-label": "Customer Center",
            className: "rc-account-v2-auth",
            children: /*#__PURE__*/ _jsx(FatalError, {
                error: fatalError
            })
        });
    }
    if (isHydrating) {
        const { hash } = window.location;
        let contentSkeleton;
        if (hash.startsWith("#/invoices")) {
            contentSkeleton = /*#__PURE__*/ _jsx(InvoicesSkeleton, {});
        } else if (hash.startsWith("#/details")) {
            contentSkeleton = /*#__PURE__*/ _jsx(DetailsSkeleton, {});
        } else {
            contentSkeleton = /*#__PURE__*/ _jsx(AccountSkeleton, {});
        }
        return /*#__PURE__*/ _jsxs("main", {
            "aria-label": "Customer Center",
            className: "rc-account-v2-auth",
            children: [
                /*#__PURE__*/ _jsx(SlimHero, {}),
                /*#__PURE__*/ _jsx(TabStrip, {}),
                /*#__PURE__*/ _jsx("div", {
                    className: "rc-account-v2-auth__content",
                    children: contentSkeleton
                })
            ]
        });
    }
    if (!isLoggedIn) {
        return /*#__PURE__*/ _jsxs("main", {
            "aria-label": "Customer Center",
            className: "rc-account-v2-unauth",
            children: [
                /*#__PURE__*/ _jsx(UnauthHero, {}),
                /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-unauth-body",
                    children: [
                        /*#__PURE__*/ _jsx(Notifications, {
                            channel: NotificationChannel.accountV2Unauth,
                            className: "rc-account-v2-unauth-notifications"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "rc-account-v2-unauth-grid",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "rc-account-v2-unauth-grid__form",
                                    children: /*#__PURE__*/ _jsx(MagicLinkSignInForm, {})
                                }),
                                /*#__PURE__*/ _jsx(UnauthFeatures, {})
                            ]
                        }),
                        /*#__PURE__*/ _jsx(UnauthAlternativePaths, {})
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("main", {
        "aria-label": "Customer Center",
        className: "rc-account-v2-auth",
        children: [
            /*#__PURE__*/ _jsx(SlimHero, {}),
            /*#__PURE__*/ _jsx(TabStrip, {}),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-auth__content",
                children: [
                    /*#__PURE__*/ _jsx(Notifications, {
                        channel: NotificationChannel.accountV2Auth,
                        className: "rc-account-v2-auth-notifications"
                    }),
                    /*#__PURE__*/ _jsx(Outlet, {})
                ]
            })
        ]
    });
});
export { App };
